import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

const KG_TO_LB = 2.2046226218;
const LB_TO_KG = 1 / KG_TO_LB;
const AVGAS_LB_PER_US_GALLON = 6;

function isValidNumber(value) {
  return (
    value !== '' &&
    value !== null &&
    value !== undefined &&
    Number.isFinite(Number(value))
  );
}

function getNumeric(value) {
  return Number.isFinite(Number(value))
    ? Number(value)
    : null;
}

function getAircraftWbConstants(aircraft) {
  if (
    aircraft?.wb_constants &&
    typeof aircraft.wb_constants === 'object'
  ) {
    return aircraft.wb_constants;
  }

  return {};
}

function normaliseLoadingStations(stations) {
  if (!Array.isArray(stations)) {
    return [];
  }

  return stations
    .map((station, index) => ({
      id:
        String(station?.id || '').trim() ||
        `station-${index}`,
      name:
        String(station?.name || '').trim() ||
        `Station ${index + 1}`,
      type:
        String(station?.type || 'other')
          .trim()
          .toLowerCase(),
      arm: getNumeric(station?.arm),
      maxWeight: getNumeric(station?.maxWeight),
    }))
    .filter((station) => station.name);
}

export function isAircraftWbConfigured(aircraft) {
  const wb = getAircraftWbConstants(aircraft);

  const loadingStations =
    normaliseLoadingStations(
      wb.loadingStations
    );

  const basicValuesValid =
    Number.isFinite(Number(wb.emptyWeight)) &&
    Number(wb.emptyWeight) > 0 &&
    Number.isFinite(Number(wb.emptyMoment)) &&
    Number(wb.emptyMoment) > 0 &&
    Number.isFinite(
      Number(wb.maxTakeoffWeight)
    ) &&
    Number(wb.maxTakeoffWeight) > 0 &&
    Number.isFinite(Number(wb.minCg)) &&
    Number.isFinite(Number(wb.maxCg)) &&
    Number(wb.maxCg) > Number(wb.minCg);

  const stationsValid =
    loadingStations.length > 0 &&
    loadingStations.every(
      (station) =>
        station.name &&
        Number.isFinite(station.arm)
    );

  return basicValuesValid && stationsValid;
}

export function getWbTemplateForAircraft(
  aircraft
) {
  const wb = getAircraftWbConstants(aircraft);

  return {
    name:
      aircraft?.registration || 'Aircraft',

    configured:
      isAircraftWbConfigured(aircraft),

    emptyWeight:
      getNumeric(wb.emptyWeight),

    emptyMoment:
      getNumeric(wb.emptyMoment),

    loadingStations:
      normaliseLoadingStations(
        wb.loadingStations
      ),

    limits: {
      maxTakeoffWeight:
        getNumeric(wb.maxTakeoffWeight),

      minCg:
        getNumeric(wb.minCg),

      maxCg:
        getNumeric(wb.maxCg),
    },
  };
}

function buildInitialLoadingValues(
  template,
  initialValues
) {
  const savedValues =
    initialValues?.loadingValues &&
    typeof initialValues.loadingValues ===
      'object'
      ? initialValues.loadingValues
      : {};

  const nextValues = {};

  template.loadingStations.forEach(
    (station) => {
      const savedValue =
        savedValues[station.id];

      nextValues[station.id] =
        isValidNumber(savedValue)
          ? String(savedValue)
          : '';
    }
  );

  return nextValues;
}

function toPounds(value, unit) {
  if (!isValidNumber(value)) {
    return 0;
  }

  const numericValue = Number(value);

  if (unit === 'kg') {
    return numericValue * KG_TO_LB;
  }

  return numericValue;
}

function formatKgFromLb(lb) {
  return (
    Number(lb || 0) * LB_TO_KG
  ).toFixed(1);
}

function formatNumber(value, decimals = 1) {
  if (!Number.isFinite(Number(value))) {
    return '-';
  }

  return Number(value).toFixed(decimals);
}

function getStationInputLabel(
  station,
  loadingUnit
) {
  if (station.type === 'fuel') {
    return 'US gal';
  }

  return loadingUnit === 'kg'
    ? 'kg'
    : 'lb';
}

function getStationWeightLb(
  station,
  enteredValue,
  loadingUnit
) {
  if (!isValidNumber(enteredValue)) {
    return 0;
  }

  if (station.type === 'fuel') {
    return (
      Number(enteredValue) *
      AVGAS_LB_PER_US_GALLON
    );
  }

  return toPounds(
    enteredValue,
    loadingUnit
  );
}

export default function WeightBalancePanel({
  aircraft,
  bookingDate,
  compact = false,
  initialValues = null,
  onResultChange = null,
  onSave = null,
}) {
  const template = useMemo(
    () =>
      getWbTemplateForAircraft(aircraft),
    [aircraft]
  );

  const [loadingUnit, setLoadingUnit] =
    useState(
      initialValues?.loadingUnit === 'lb'
        ? 'lb'
        : 'kg'
    );

  const [
    loadingValues,
    setLoadingValues,
  ] = useState(() =>
    buildInitialLoadingValues(
      template,
      initialValues
    )
  );

  useEffect(() => {
    const nextTemplate =
      getWbTemplateForAircraft(aircraft);

    setLoadingUnit(
      initialValues?.loadingUnit === 'lb'
        ? 'lb'
        : 'kg'
    );

    setLoadingValues(
      buildInitialLoadingValues(
        nextTemplate,
        initialValues
      )
    );
  }, [
    aircraft,
    bookingDate,
    initialValues,
  ]);

  function updateLoadingValue(
    stationId,
    value
  ) {
    setLoadingValues(
      (currentValues) => ({
        ...currentValues,
        [stationId]: value,
      })
    );
  }

  const stationResults = useMemo(
    () =>
      template.loadingStations.map(
        (station) => {
          const enteredValue =
            loadingValues[station.id] ?? '';

          const entered =
            isValidNumber(enteredValue);

          const weightLb =
            getStationWeightLb(
              station,
              enteredValue,
              loadingUnit
            );

          const moment =
            weightLb *
            Number(station.arm || 0);

          const exceedsStationMaximum =
            entered &&
            Number.isFinite(
              station.maxWeight
            ) &&
            station.maxWeight > 0 &&
            weightLb >
              station.maxWeight;

          return {
            ...station,
            enteredValue,
            entered,
            weightLb,
            moment,
            exceedsStationMaximum,
          };
        }
      ),
    [
      template.loadingStations,
      loadingValues,
      loadingUnit,
    ]
  );

  const loadingWeight = stationResults.reduce(
    (total, station) =>
      total + station.weightLb,
    0
  );

  const loadingMoment =
    stationResults.reduce(
      (total, station) =>
        total + station.moment,
      0
    );

  const totalWeight =
    Number(template.emptyWeight || 0) +
    loadingWeight;

  const totalMoment =
    Number(template.emptyMoment || 0) +
    loadingMoment;

  const cg =
    totalWeight > 0
      ? totalMoment / totalWeight
      : 0;

  const requiredFieldsComplete =
    stationResults.length > 0 &&
    stationResults.every(
      (station) => station.entered
    );

  const overweight =
    requiredFieldsComplete &&
    totalWeight >
      Number(
        template.limits
          .maxTakeoffWeight || 0
      );

  const cgTooFarForward =
    requiredFieldsComplete &&
    cg <
      Number(
        template.limits.minCg || 0
      );

  const cgTooFarAft =
    requiredFieldsComplete &&
    cg >
      Number(
        template.limits.maxCg || 0
      );

  const cgOutOfRange =
    cgTooFarForward || cgTooFarAft;

  const stationLimitExceeded =
    stationResults.some(
      (station) =>
        station.exceedsStationMaximum
    );

  const withinLimits =
    template.configured &&
    requiredFieldsComplete &&
    !overweight &&
    !cgOutOfRange &&
    !stationLimitExceeded;

  const remainingPayload =
    Number(
      template.limits
        .maxTakeoffWeight || 0
    ) - totalWeight;

  useEffect(() => {
    if (
      typeof onResultChange !==
      'function'
    ) {
      return;
    }

    onResultChange({
      configured: template.configured,
      complete:
        requiredFieldsComplete,
      withinLimits,
      overweight,
      cgOutOfRange,
      cgTooFarForward,
      cgTooFarAft,
      stationLimitExceeded,
      totalWeight,
      totalMoment,
      cg,
      remainingPayload,
      loadingUnit,
      loadingValues,
      stationResults:
        stationResults.map(
          (station) => ({
            id: station.id,
            name: station.name,
            type: station.type,
            arm: station.arm,
            enteredValue:
              station.enteredValue,
            weightLb:
              station.weightLb,
            moment:
              station.moment,
            maxWeight:
              station.maxWeight,
            exceedsStationMaximum:
              station.exceedsStationMaximum,
          })
        ),
    });
  }, [
    onResultChange,
    template.configured,
    requiredFieldsComplete,
    withinLimits,
    overweight,
    cgOutOfRange,
    cgTooFarForward,
    cgTooFarAft,
    stationLimitExceeded,
    totalWeight,
    totalMoment,
    cg,
    remainingPayload,
    loadingUnit,
    loadingValues,
    stationResults,
  ]);

  if (!template.configured) {
    return (
      <div style={styles.infoPanel}>
        <div style={styles.infoTitle}>
          Aircraft Weight &amp; Balance not configured
        </div>

        <div style={styles.infoText}>
          The aircraft owner has not yet configured this aircraft's Weight &amp; Balance data. Wingit cannot calculate loading until this has been completed.
        </div>

                <div
          style={styles.infoSecondary}
        >
          This check will become
          available automatically once
          the aircraft owner has entered
          the approved Weight &amp;
          Balance figures and loading
          stations in Admin.
        </div>
      </div>
    );
  }

  const resultMeta = getResultMeta({
    requiredFieldsComplete,
    withinLimits,
  });

  const inputStyle = {
    ...styles.input,
    width: compact ? 95 : 115,
  };

  return (
    <div
      style={{
        ...styles.panel,
        padding: compact
          ? '1rem 1.1rem'
          : '1.5rem',
        marginBottom: compact
          ? '1.4rem'
          : '2rem',
      }}
    >
      <h3 style={styles.heading}>
        Weight &amp; Balance
      </h3>

      <div
        style={styles.aircraftLine}
      >
        Aircraft:{' '}
        <strong>
          {aircraft?.registration ||
            'N/A'}
        </strong>

        {bookingDate ? (
          <span
            style={styles.bookingDate}
          >
            {' '}
            for {bookingDate}
          </span>
        ) : null}
      </div>

      <div
        style={styles.supportingText}
      >
        Enter the actual loading for
        this flight. Wingit will use the
        loading stations configured by
        the aircraft owner.
      </div>

      <div style={styles.unitRow}>
        <label
          htmlFor="loadingUnit"
          style={styles.unitLabel}
        >
          Passenger, baggage and
          equipment units:
        </label>

        <select
          id="loadingUnit"
          value={loadingUnit}
          onChange={(event) =>
            setLoadingUnit(
              event.target.value
            )
          }
          style={styles.select}
        >
          <option value="kg">
            Kilograms (kg)
          </option>

          <option value="lb">
            Pounds (lb)
          </option>
        </select>
      </div>

      <div style={styles.notice}>
        Fuel stations are entered in US
        gallons. Wingit uses 6 lb per US
        gallon for the calculation.
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                Loading station
              </th>

              <th style={styles.th}>
                Weight / Quantity
              </th>

              <th style={styles.th}>
                Arm
              </th>

              <th style={styles.th}>
                Moment
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td style={styles.td}>
                Empty aircraft
              </td>

              <td style={styles.td}>
                {formatNumber(
                  template.emptyWeight
                )}{' '}
                lb
                <div
                  style={styles.smallText}
                >
                  {formatKgFromLb(
                    template.emptyWeight
                  )}{' '}
                  kg
                </div>
              </td>

              <td style={styles.td}>
                -
              </td>

              <td style={styles.td}>
                {formatNumber(
                  template.emptyMoment
                )}
              </td>
            </tr>

            {stationResults.map(
              (station) => {
                const inputLabel =
                  getStationInputLabel(
                    station,
                    loadingUnit
                  );

                return (
                  <tr key={station.id}>
                    <td style={styles.td}>
                      <div
                        style={
                          styles.stationName
                        }
                      >
                        {station.name}
                      </div>

                      <div
                        style={
                          styles.stationType
                        }
                      >
                        {getStationTypeLabel(
                          station.type
                        )}
                      </div>

                      {Number.isFinite(
                        station.maxWeight
                      ) &&
                      station.maxWeight > 0 ? (
                        <div
                          style={
                            styles.smallText
                          }
                        >
                          Maximum{' '}
                          {formatNumber(
                            station.maxWeight
                          )}{' '}
                          lb
                        </div>
                      ) : null}
                    </td>

                    <td style={styles.td}>
                      <div
                        style={
                          styles.inputRow
                        }
                      >
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={
                            station.enteredValue
                          }
                          onChange={(
                            event
                          ) =>
                            updateLoadingValue(
                              station.id,
                              event.target
                                .value
                            )
                          }
                          placeholder={
                            inputLabel
                          }
                          style={
                            station.exceedsStationMaximum
                              ? {
                                  ...inputStyle,
                                  border:
                                    '1px solid #ff8d8d',
                                }
                              : inputStyle
                          }
                        />

                        <span
                          style={
                            styles.inputUnit
                          }
                        >
                          {inputLabel}
                        </span>
                      </div>

                      {station.type ===
                        'fuel' &&
                      station.entered ? (
                        <div
                          style={
                            styles.smallText
                          }
                        >
                          {formatNumber(
                            station.weightLb
                          )}{' '}
                          lb fuel weight
                        </div>
                      ) : null}

                      {station.exceedsStationMaximum ? (
                        <div
                          style={
                            styles.fieldError
                          }
                        >
                          Station maximum
                          exceeded
                        </div>
                      ) : null}
                    </td>

                    <td style={styles.td}>
                      {formatNumber(
                        station.arm
                      )}{' '}
                      in
                    </td>

                    <td style={styles.td}>
                      {formatNumber(
                        station.moment
                      )}{' '}
                      in-lb
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div
            style={styles.summaryLabel}
          >
            Total weight
          </div>

          <div
            style={styles.summaryValue}
          >
            {formatNumber(
              totalWeight
            )}{' '}
            lb
          </div>

          <div
            style={styles.summarySecondary}
          >
            {formatKgFromLb(
              totalWeight
            )}{' '}
            kg
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div
            style={styles.summaryLabel}
          >
            Maximum take-off weight
          </div>

          <div
            style={styles.summaryValue}
          >
            {formatNumber(
              template.limits
                .maxTakeoffWeight
            )}{' '}
            lb
          </div>

          <div
            style={styles.summarySecondary}
          >
            {formatKgFromLb(
              template.limits
                .maxTakeoffWeight
            )}{' '}
            kg
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div
            style={styles.summaryLabel}
          >
            Centre of gravity
          </div>

          <div
            style={styles.summaryValue}
          >
            {formatNumber(cg, 2)} in
          </div>

          <div
            style={styles.summarySecondary}
          >
            Allowed range{' '}
            {formatNumber(
              template.limits.minCg
            )}{' '}
            –{' '}
            {formatNumber(
              template.limits.maxCg
            )}{' '}
            in
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div
            style={styles.summaryLabel}
          >
            Remaining payload
          </div>

          <div
            style={{
              ...styles.summaryValue,
              color:
                remainingPayload < 0
                  ? '#ff9c9c'
                  : '#eaf6ff',
            }}
          >
            {formatNumber(
              remainingPayload
            )}{' '}
            lb
          </div>

          <div
            style={styles.summarySecondary}
          >
            {formatKgFromLb(
              remainingPayload
            )}{' '}
            kg
          </div>
        </div>
      </div>

      <div
        style={{
          ...styles.resultPanel,
          border: `1px solid ${resultMeta.border}`,
          background:
            resultMeta.background,
        }}
      >
        <div
          style={{
            ...styles.resultTitle,
            color: resultMeta.colour,
          }}
        >
          {resultMeta.title}
        </div>

        <div style={styles.resultText}>
          {resultMeta.message}
        </div>

        {requiredFieldsComplete &&
        !withinLimits ? (
          <div style={styles.issueList}>
            {overweight ? (
              <div>
                Maximum take-off weight
                exceeded by{' '}
                {formatNumber(
                  Math.abs(
                    remainingPayload
                  )
                )}{' '}
                lb.
              </div>
            ) : null}

            {cgTooFarForward ? (
              <div>
                Centre of gravity is too
                far forward.
              </div>
            ) : null}

            {cgTooFarAft ? (
              <div>
                Centre of gravity is too
                far aft.
              </div>
            ) : null}

            {stationResults
              .filter(
                (station) =>
                  station.exceedsStationMaximum
              )
              .map((station) => (
                <div key={station.id}>
                  {station.name} exceeds
                  its maximum permitted
                  weight.
                </div>
              ))}
          </div>
               ) : null}
      </div>

      {withinLimits &&
      typeof onSave === 'function' ? (
        <div
          style={{
            marginTop: 16,
            textAlign: 'right',
          }}
        >
          <button
            type="button"
            onClick={onSave}
            style={{
              background: '#2d7ff9',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 18px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Save W&amp;B and Return
          </button>
        </div>
      ) : null}
    </div>
  );
}

function getStationTypeLabel(type) {
  if (type === 'occupants') {
    return 'Seats';
  }

  if (type === 'fuel') {
    return 'Fuel';
  }

  if (type === 'baggage') {
    return 'Baggage';
  }

  return 'Other loading';
}

function getResultMeta({
  requiredFieldsComplete,
  withinLimits,
}) {
  if (!requiredFieldsComplete) {
    return {
      title:
        'Weight & Balance incomplete',
      message:
        'Enter a value for every loading station. Enter zero where a station is not being used.',
      colour: '#ffd27a',
      border:
        'rgba(255,210,122,0.28)',
      background:
        'rgba(255,210,122,0.07)',
    };
  }

  if (withinLimits) {
    return {
      title:
        'Weight & Balance within limits',
      message:
        'Aircraft loading is within the approved weight, station and centre-of-gravity limits.',
      colour: '#8cff8c',
      border:
        'rgba(140,255,140,0.24)',
      background:
        'rgba(140,255,140,0.07)',
    };
  }

  return {
    title:
      'Weight & Balance outside limits',
    message:
      'Aircraft loading is outside one or more approved limits and cannot be completed.',
    colour: '#ff9c9c',
    border:
      'rgba(255,156,156,0.28)',
    background:
      'rgba(255,156,156,0.07)',
  };
}

const styles = {
  panel: {
    background: '#1a2338',
    border: '1px solid #35507f',
    borderRadius: 12,
    color: '#b6eaff',
  },

  infoPanel: {
    background:
      'rgba(85,199,255,0.05)',
    border:
      '1px solid rgba(85,199,255,0.18)',
    borderRadius: 12,
    padding: '1rem',
    marginBottom: '1.4rem',
  },

  infoTitle: {
    color: '#55c7ff',
    fontSize: '0.96rem',
    fontWeight: 800,
    marginBottom: '0.5rem',
  },

  infoText: {
    color: '#d7ebff',
    lineHeight: 1.55,
    fontSize: '0.88rem',
  },

  infoSecondary: {
    marginTop: '0.6rem',
    color: '#8db7e6',
    lineHeight: 1.55,
    fontSize: '0.84rem',
  },

  heading: {
    color: '#55c7ff',
    margin: '0 0 0.8rem',
    fontSize: '1rem',
    fontWeight: 800,
  },

  aircraftLine: {
    marginBottom: 7,
    color: '#d7ebff',
    fontSize: '0.88rem',
  },

  bookingDate: {
    color: '#8db7e6',
  },

  supportingText: {
    marginBottom: 10,
    color: '#8db7e6',
    fontSize: '0.84rem',
    lineHeight: 1.45,
  },

  unitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 10,
  },

  unitLabel: {
    color: '#d7ebff',
    fontWeight: 700,
    fontSize: '0.84rem',
  },

  select: {
    padding: '0.4rem 0.55rem',
    borderRadius: 8,
    border: '1px solid #4d79a8',
    background: '#10233a',
    color: '#ffffff',
    fontWeight: 700,
  },

  notice: {
    marginBottom: '1rem',
    padding: '0.65rem 0.75rem',
    border:
      '1px solid rgba(85,199,255,0.18)',
    borderRadius: 8,
    background:
      'rgba(85,199,255,0.05)',
    color: '#8db7e6',
    fontSize: '0.8rem',
    lineHeight: 1.45,
  },

  tableWrap: {
    overflowX: 'auto',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.86rem',
  },

  th: {
    textAlign: 'left',
    borderBottom:
      '1px solid #2f466d',
    padding: '0 0.5rem 0.55rem 0',
    color: '#8db7e6',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
  },

  td: {
    padding: '0.65rem 0.5rem 0.65rem 0',
    borderBottom:
      '1px solid rgba(47,70,109,0.55)',
    verticalAlign: 'middle',
    color: '#d7ebff',
  },

  stationName: {
    fontWeight: 700,
    color: '#eaf6ff',
  },

  stationType: {
    marginTop: 2,
    color: '#8db7e6',
    fontSize: '0.75rem',
  },

  smallText: {
    marginTop: 3,
    color: '#8db7e6',
    fontSize: '0.74rem',
  },

  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  input: {
    padding: '0.4rem 0.5rem',
    borderRadius: 6,
    border: '1px solid #2f466d',
    background: '#181a2a',
    color: '#eaf6ff',
    boxSizing: 'border-box',
  },

  inputUnit: {
    color: '#8db7e6',
    fontSize: '0.76rem',
    whiteSpace: 'nowrap',
  },

  fieldError: {
    marginTop: 4,
    color: '#ff9c9c',
    fontSize: '0.74rem',
  },

  summaryGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(165px, 1fr))',
    gap: 10,
    marginTop: 14,
  },

  summaryCard: {
    border: '1px solid #2f466d',
    borderRadius: 10,
    padding: '0.75rem',
    background: '#12182a',
  },

  summaryLabel: {
    color: '#8db7e6',
    fontSize: '0.75rem',
    marginBottom: 4,
  },

  summaryValue: {
    color: '#eaf6ff',
    fontSize: '1rem',
    fontWeight: 800,
  },

  summarySecondary: {
    marginTop: 3,
    color: '#8db7e6',
    fontSize: '0.74rem',
  },

  resultPanel: {
    marginTop: 12,
    borderRadius: 10,
    padding: '0.85rem',
  },

  resultTitle: {
    fontWeight: 800,
    fontSize: '0.9rem',
    marginBottom: 4,
  },

  resultText: {
    color: '#d7ebff',
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },

  issueList: {
    marginTop: 8,
    color: '#ffb0b0',
    fontSize: '0.8rem',
    lineHeight: 1.55,
  },
};